import React, { useEffect, useState } from "react";
import { BookOpen, ExternalLink, Shield, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

// Book cover fallback — swap via Admin → Page Images → Resources → book-cover.
// Original WP-hosted URL died during DNS cutover; substituted with a working
// Emergent-hosted portrait so the page never breaks.
const BOOK_FALLBACK = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/exlvccso_Holly%202014%20Passions%20Production%20Co-95.jpg";

const AFFILIATES = [
  {
    id: "insurance",
    icon: Shield,
    label: "Aerial Yoga Liability Insurance",
    body: "Affordable, comprehensive coverage for aerial yoga instructors and studios. The same partner I trust for Aviana — fast quotes, no-hassle renewals, and the safety net every teacher should have before they ever hang a hammock.",
    cta: "Get a quote",
    url: "https://alternativebalance.com#5c7ef45db5b7f",
    badge: "Aviana Affiliate · Alternative Balance",
    keywords: "aerial yoga insurance · yoga teacher liability · studio coverage",
  },
  {
    id: "income",
    icon: Sparkles,
    label: "The Income Potential of an Aerial Yoga Instructor",
    body: "A detailed essay on multiple revenue streams available to certified aerial teachers — from privates to retreats to digital products.",
    cta: "Read essay",
    url: "https://www.avianayoga.com/the-income-potential-of-an-aerial-yoga-instructor-unlocking-multiple-revenue-streams/",
    badge: "Aviana Journal",
  },
];

export default function Resources() {
  const [book, setBook] = useState(BOOK_FALLBACK);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/page-images/resources");
        const slot = data?.slots?.["book-cover"]?.[0];
        if (slot) setBook(slot);
      } catch {}
    })();
  }, []);
  return (
    <div className="pt-36 pb-24" data-testid="resources-page">
      <SEO
        title="Aerial Yoga Resources — Soaring With the Soul, Insurance & Income Guide"
        description="Trusted resources for aerial yoga teachers and students — Holly Johnson's book Soaring With the Soul (2nd edition, 100+ poses), recommended aerial yoga liability insurance partner, and our essay on the multiple income streams open to certified aerial teachers."
        path="/resources"
        keywords="aerial yoga insurance, Soaring with the Soul book, aerial yoga income, aerial yoga book, Holly Johnson book, aerial yoga liability insurance, yoga teacher resources"
      />
      <div className="container-aviana">
        <p className="overline mb-5">For Your Practice</p>
        <h1 className="font-display text-6xl md:text-7xl text-aviana-ink max-w-4xl leading-[0.98]">
          Books, essays & <em className="italic font-serif gold-text">resources.</em>
        </h1>

        {/* BOOK FEATURE */}
        <section className="mt-20 grid lg:grid-cols-12 gap-14 items-center" data-testid="resources-book">
          <div className="lg:col-span-5">
            <div className="img-zoom aspect-[3/4] rounded-[2rem] ring-1 ring-aviana-gold/30 max-w-md">
              <img src={book} alt="Soaring With the Soul" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-4">Now Available · 2nd Edition</p>
            <h2 className="font-display text-5xl text-aviana-ink leading-[1.02]">Soaring With the Soul</h2>
            <p className="mt-3 font-serif italic text-xl text-aviana-muted">An Aviana Aerial Yoga Guide</p>
            <p className="mt-7 text-aviana-muted leading-[1.85] max-w-xl">
              Over 100 blissful aerial yoga poses — from grounding basics to exhilarating flows.
              The second edition refines fourteen years of teaching into a single living transmission.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="https://amzn.to/4ttObU4" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="book-amazon-cta">
                <BookOpen size={16} /> Order on Amazon
              </a>
              <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted self-center">
                As an Amazon Associate I earn from qualifying purchases.
              </span>
            </div>
          </div>
        </section>

        <div className="gold-divider my-24" />

        {/* AFFILIATES & RESOURCES */}
        <section data-testid="affiliates">
          <p className="overline mb-5">Trusted Partners</p>
          <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
            Tools I <em className="italic font-serif gold-text">recommend.</em>
          </h2>
          <p className="mt-6 max-w-2xl text-aviana-muted leading-[1.85]">
            A short, vetted list of partners that support aerial teachers and retreat-goers. Some links
            are affiliate links — meaning if you purchase through them, Aviana earns a small commission
            at no extra cost to you.
          </p>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {AFFILIATES.map((a) => {
              const Icon = a.icon;
              return (
                <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"
                   className="card-aviana p-10 group block" data-testid={`affiliate-${a.id}`}>
                  <div className="flex items-start justify-between mb-6">
                    <Icon size={28} strokeWidth={1.3} className="text-aviana-gold" />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-aviana-plum border border-aviana-plum/30 rounded-full px-3 py-1">{a.badge}</span>
                  </div>
                  <h3 className="font-display text-2xl text-aviana-ink mb-3">{a.label}</h3>
                  <p className="text-sm text-aviana-muted leading-[1.75] mb-3">{a.body}</p>
                  {a.keywords && (
                    <p className="text-[10px] tracking-[0.16em] uppercase text-aviana-muted/60 mb-7">{a.keywords}</p>
                  )}
                  <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-aviana-gold group-hover:gap-3 transition-all">
                    {a.cta} <ExternalLink size={12} />
                  </span>
                </a>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
